import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { keyOutline, createOutline, trashOutline, copyOutline } from 'ionicons/icons';
import DataTable from '../../components/shared/DataTable/DataTable';
import { SkeletonTable } from '../../components/atoms';
import LetterAvatar from '../../components/atoms/LetterAvatar/LetterAvatar';
import AgentHoneycomb from '../../components/atoms/AgentHoneycomb/AgentHoneycomb';
import { useProjectAgents } from '../../hooks/useProjectAgents';

const ProjectsList = ({ 
  projects = [], 
  loading = false, 
  onEdit, 
  onDelete,
  onCopyApiKey,
  onAdd
}) => {
  const navigate = useNavigate();
  // Deployed agents per project → the honeycomb in the NAME cell. Only fetched
  // for projects the server already reported as having agents, and cached under
  // the same key the Agents page reads, so this costs nothing for empty
  // projects and warms the list the user is about to open.
  const agentsByProject = useProjectAgents(projects);

  const maskToken = (token) => {
    if (!token) return '—';
    const prefix = token.substring(0, 4);
    const suffix = token.substring(token.length - 4);
    return `${prefix}...${suffix}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const copyPublicId = async (publicId) => {
    if (!publicId) return;
    try {
      await navigator.clipboard.writeText(publicId);
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  const columns = [
    {
      header: 'Name',
      key: 'name',
      className: 'data-table-name',
      render: (project) => (
        <span className="projects-name-cell">
          <LetterAvatar name={project.name} />
          <span>{project.name}</span>
          <AgentHoneycomb
            agents={(agentsByProject[project.projectId] || []).map((w) => ({
              key: w.uuid || w.workflowType,
              iconUrl: w.iconUrl,
              label: w.displayName || w.workflowType,
            }))}
          />
        </span>
      )
    },
    {
      header: 'ID',
      key: 'publicId',
      className: 'data-table-token data-table-id',
      width: '110px',
      render: (project) => (
        project.publicId ? (
          <span
            className="token-copyable"
            title="Click to copy project ID"
            onClick={(e) => {
              e.stopPropagation();
              copyPublicId(project.publicId);
            }}
          >
            <span className="token-text">{project.publicId}</span>
            <IonIcon icon={copyOutline} className="token-copy-icon" />
          </span>
        ) : (
          <span className="token-text" style={{ opacity: 0.4 }}>—</span>
        )
      )
    },
    {
      header: 'Token',
      key: 'apiToken',
      className: 'data-table-token',
      render: (project) => (
        <span
          className="token-copyable"
          title="Click to copy"
          onClick={(e) => {
            e.stopPropagation();
            onCopyApiKey(project);
          }}
        >
          <span className="token-text">{maskToken(project.apiToken)}</span>
          <IonIcon icon={copyOutline} className="token-copy-icon" />
        </span>
      )
    },
    {
      header: 'Created',
      key: 'createdAt',
      className: 'data-table-date',
      render: (project) => formatDate(project.createdAt)
    }
  ];

  const actions = [
    {
      label: 'Copy API Key',
      icon: keyOutline,
      onClick: onCopyApiKey
    },
    {
      label: 'Edit',
      icon: createOutline,
      onClick: onEdit
    },
    {
      divider: true
    },
    {
      label: 'Delete',
      icon: trashOutline,
      onClick: onDelete,
      danger: true,
    }
  ];

  if (loading) {
    return (
      <SkeletonTable
        columns={[
          { headerWidth: '60px', width: '180px', height: '14px' },
          { headerWidth: '80px', width: '140px', height: '14px' },
          { headerWidth: '70px', width: '90px', height: '14px' },
          { thWidth: '40px', width: '24px', height: '24px', borderRadius: '50%' }
        ]}
        rowCount={5}
        label="Loading"
      />
    );
  }

  // The wrapper is the SCOPE for this table's taller rows (ProjectsPage.css) —
  // the agent honeycomb overflows its cell by design and needs the headroom,
  // and no other DataTable should inherit it.
  return (
    <div className="projects-table-wrap">
      <DataTable
        data={projects}
        columns={columns}
        loading={loading}
        rowKey="projectId"
        onRowClick={(project) => navigate(`/projects/${project.projectId}/pipelines`)}
        actions={actions}
        emptyState={{
          title: "No Projects Yet",
          description: "Create your first project to get started.",
          actionLabel: "Add",
          onAction: onAdd
        }}
      />
    </div>
  );
};

export default ProjectsList;
